import { useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { WithEnterpriseId } from '@mews-commander/types';
import { DateService, DateTimeService, lt, useAppClockService, useTimezone } from '@mews/framework';
import { Card, Container, Stack, StackItem, Typography } from '@optimus-web/core';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedShortDateFromUtc } from '@mews-commander/ui';
import { RewardBanner } from './RewardBanner';
import { GoLiveDatePicker } from './goLiveDatePicker/GoLiveDatePicker';
import { GoLiveDateBanner } from './GoLiveDateBanner';

export const PropertyTimeline = ({ enterpriseId }: WithEnterpriseId) => {
    const appClockService = useAppClockService();
    const timezone = useTimezone();
    const enterprise = useEnterprise(enterpriseId);

    const { data } = useRepositorySuspenseQuery('enterprises', 'getOnboardingDetails', {});

    if (enterprise.isOnboardingFinished) return null;

    const nowDateTime = DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone);
    const nowDate = DateTimeService.toDate(nowDateTime);
    const createdDateTime = DateTimeService.fromUtcString(enterprise.createdUtc, timezone);
    const createdDate = DateTimeService.toDate(createdDateTime);
    const days = DateService.diffDates(nowDate, createdDate);

    const goLiveDateTime = data.goLiveDate ? DateTimeService.fromUtcString(data.goLiveDate, timezone) : null;
    const goLiveDate = goLiveDateTime ? DateTimeService.toDate(goLiveDateTime) : null;
    const daysToGoLive = goLiveDate ? DateService.diffDates(goLiveDate, nowDate) : null;
    const nonNegativeDaysToGolive = daysToGoLive !== null ? Math.max(0, daysToGoLive) : null;

    /* Show the current days in onboarding, the first day is 0 + 1 */
    const daysInOnboarding = days + 1;

    return (
        <Card>
            <Container padding="300">
                <Stack spacing="200" vertical>
                    <Stack vertical>
                        <Stack distribution="space-between">
                            <StackItem>
                                <Typography textStyle="bodyLargeStrong" as="div">
                                    {lt('Timeline')}
                                </Typography>
                                {data.goLiveDate ? (
                                    <Typography textStyle="bodySmall" variant="tertiary" as="div">
                                        {lt('OnboardingAuditTimelineGoLiveDate', {
                                            date: <LocalizedShortDateFromUtc utcString={data.goLiveDate} />,
                                        })}
                                    </Typography>
                                ) : null}
                            </StackItem>
                            <GoLiveDatePicker />
                        </Stack>
                    </Stack>
                    <Stack distribution="stretch">
                        <StackItem>
                            <Typography textStyle="highlightMedium" as="div">
                                {daysInOnboarding}
                            </Typography>
                            <Typography textStyle="bodySmall" variant="tertiary" as="div">
                                {lt('OnboardingAuditTimelineDaysInOnboarding', { count: daysInOnboarding })}
                            </Typography>
                        </StackItem>
                        <StackItem>
                            <Typography textStyle="highlightMedium" as="div">
                                {nonNegativeDaysToGolive !== null ? nonNegativeDaysToGolive : UnicodeEntity.Dash}
                            </Typography>
                            <Typography textStyle="bodySmall" variant="tertiary" as="div">
                                {lt('OnboardingAuditTimelineDaysToGoLive', {
                                    count: nonNegativeDaysToGolive !== null ? nonNegativeDaysToGolive : 0,
                                })}
                            </Typography>
                        </StackItem>
                    </Stack>
                    <RewardBanner daysInOnboarding={daysInOnboarding} />
                    {daysToGoLive !== null && (
                        <>
                            <GoLiveDateBanner
                                variant="warning"
                                bannerText={lt('OnboardingAuditTimelineGoLiveBannerWarning')}
                                condition={daysToGoLive > 0 && daysToGoLive <= 7}
                            />
                            <GoLiveDateBanner
                                variant="error"
                                bannerText={lt('OnboardingAuditTimelineGoLiveBannerError')}
                                condition={daysToGoLive <= 0}
                            />
                        </>
                    )}
                </Stack>
            </Container>
        </Card>
    );
};
